import type { ReactNode } from 'react';
import { MotionConfig, type Transition } from 'motion/react';

/**
 * Tokens de movimento (espelham theme.css). Regra da marca: molas curtas e firmes, entradas de
 * 150 a 300 ms, nada que atrase uma venda. Sempre respeitar prefers-reduced-motion.
 */
export const durations = {
  /** feedback imediato (hover, pressionar) */
  instant: 0.09,
  fast: 0.15,
  base: 0.2,
  /** entradas de folhas e listas */
  slow: 0.3,
} as const;

/** Curvas cubic-bezier (mesmos valores das variáveis --ease-* do CSS). */
export const easings = {
  standard: [0.2, 0, 0, 1],
  snappy: [0.32, 0.72, 0, 1],
  spring: [0.34, 1.32, 0.64, 1],
} as const satisfies Record<string, [number, number, number, number]>;

/** Molas prontas para o `transition` do motion. */
export const springs = {
  /** indicadores de abas e seleções que deslizam */
  snappy: { type: 'spring', stiffness: 520, damping: 40, mass: 0.9 },
  /** folhas, diálogos e cartões entrando */
  gentle: { type: 'spring', stiffness: 380, damping: 34, mass: 1 },
  /** botão do Switch: pequeno rebote */
  bouncy: { type: 'spring', stiffness: 700, damping: 30, mass: 0.8 },
} as const satisfies Record<string, Transition>;

/** Transições nomeadas reutilizáveis. */
export const transitions = {
  fade: { duration: durations.fast, ease: easings.standard },
  enter: { duration: durations.slow, ease: easings.snappy },
  layout: springs.snappy,
} as const satisfies Record<string, Transition>;

/**
 * Provedor de movimento do app: desliga animações de transformação para quem pediu
 * "reduzir movimento" no sistema. Coloque perto da raiz (App.tsx).
 */
export function MotionProvider({ children }: { children: ReactNode }) {
  return <MotionConfig reducedMotion="user">{children}</MotionConfig>;
}
